import React from "react";

/**
 * Hetvi — cute, fair-skinned girl illustration.
 * mood: "silhouette" | "sad" | "neutral" | "happy"
 */
export const HetviGirl = ({ mood = "sad", className = "" }) => {
  const isSilhouette = mood === "silhouette";
  const isSad = mood === "sad";
  const isHappy = mood === "happy";

  // Fair, peachy skin palette
  const skin = isSilhouette ? "#0b0509" : "#fbe2cf";
  const skinShade = isSilhouette ? "#0b0509" : "#f1c9b0";
  const skinDeep = isSilhouette ? "#0b0509" : "#e3a98b";
  const hair = isSilhouette ? "#040203" : "#171012";
  const hairHi = isSilhouette ? "#040203" : "#2a1d22";
  const top = isSilhouette ? "#0b0509" : "#3a221a";
  const topHi = isSilhouette ? "#0b0509" : "#4a2c22";
  const jeans = isSilhouette ? "#0b0509" : "#3a4d70";
  const jeansShade = isSilhouette ? "#0b0509" : "#2a3a58";
  const gold = isSilhouette ? "#0b0509" : "#e9c66b";
  const lip = isSilhouette ? "#0b0509" : "#e26a78";
  const lipDeep = isSilhouette ? "#0b0509" : "#b8404c";
  const sunglass = isSilhouette ? "#0b0509" : "#1a0f14";
  const browColor = isSilhouette ? "#0b0509" : "#2a1814";

  return (
    <svg
      viewBox="0 0 260 520"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      data-testid="hetvi-character"
      aria-label="Hetvi"
    >
      <ellipse cx="130" cy="510" rx="68" ry="6" fill="#000" opacity="0.35" />

      {/* ---------- HAIR BACK ---------- */}
      <path
        d={
          isHappy
            ? "M68 130 C 36 230, 30 360, 52 480 L 92 488 C 82 360, 88 250, 108 168 Z M192 130 C 224 230, 230 360, 208 480 L 168 488 C 178 360, 172 250, 152 168 Z"
            : "M72 135 C 42 250, 42 390, 68 495 L 100 500 C 90 385, 95 265, 115 175 Z M188 135 C 218 250, 218 390, 192 495 L 160 500 C 170 385, 165 265, 145 175 Z"
        }
        fill={hair}
      />

      {/* ---------- LEGS / JEANS ---------- */}
      {isSad ? (
        <>
          <path d="M94 358 Q 88 432, 100 472 L 134 472 Q 138 408, 132 358 Z" fill={jeans} />
          <path d="M128 358 Q 130 408, 128 472 L 166 472 Q 172 432, 166 358 Z" fill={jeansShade} />
          <ellipse cx="115" cy="479" rx="20" ry="6.5" fill="#1a1a1a" />
          <ellipse cx="155" cy="479" rx="20" ry="6.5" fill="#1a1a1a" />
        </>
      ) : (
        <>
          <path d="M100 322 L 95 492 L 125 492 L 128 322 Z" fill={jeans} />
          <path d="M132 322 L 135 492 L 165 492 L 160 322 Z" fill={jeansShade} />
          <ellipse cx="110" cy="497" rx="16" ry="6" fill="#1a1a1a" />
          <ellipse cx="150" cy="497" rx="16" ry="6" fill="#1a1a1a" />
        </>
      )}

      {/* ---------- TORSO ---------- */}
      <path
        d={
          isSad
            ? "M94 252 Q 88 312, 96 356 L 164 356 Q 172 312, 166 252 Q 148 242, 130 242 Q 112 242, 94 252 Z"
            : "M94 242 Q 88 292, 96 332 L 164 332 Q 172 292, 166 242 Q 148 232, 130 232 Q 112 232, 94 242 Z"
        }
        fill={top}
      />
      <path
        d={
          isSad
            ? "M102 250 Q 130 244, 158 250 L 156 258 Q 130 252, 104 258 Z"
            : "M102 240 Q 130 234, 158 240 L 156 248 Q 130 242, 104 248 Z"
        }
        fill={topHi}
        opacity={isSilhouette ? "0" : "0.8"}
      />
      {/* collarbone hint */}
      {!isSilhouette && !isSad && (
        <path
          d="M108 234 Q 130 240, 152 234"
          stroke={skinDeep}
          strokeWidth="0.8"
          fill="none"
          opacity="0.5"
        />
      )}

      {/* ---------- ARMS ---------- */}
      {isHappy ? (
        <>
          <path
            d="M96 245 Q 52 200, 26 130 Q 23 121, 33 117 Q 43 117, 53 139 Q 76 200, 106 245 Z"
            fill={skin}
          />
          <path
            d="M164 245 Q 208 200, 234 130 Q 237 121, 227 117 Q 217 117, 207 139 Q 184 200, 154 245 Z"
            fill={skin}
          />
          <circle cx="29" cy="118" r="9" fill={skinShade} />
          <circle cx="231" cy="118" r="9" fill={skinShade} />
          {/* Rose in right hand */}
          <g transform="translate(231 108)">
            <circle r="11" fill="#c0102f" />
            <circle r="7" fill="#ff4d6d" />
            <circle r="3" fill="#ff8fab" />
            <path d="M -2 10 Q -8 30, -2 45" stroke="#1f5132" strokeWidth="2.5" fill="none" />
            <path d="M 4 22 Q 12 24, 14 18" stroke="#1f5132" strokeWidth="2" fill="none" />
          </g>
        </>
      ) : isSad ? (
        <>
          <path
            d="M94 256 Q 76 312, 70 366 Q 68 374, 76 376 Q 86 376, 90 360 Q 98 312, 104 268 Z"
            fill={skin}
          />
          <path
            d="M166 256 Q 184 312, 190 366 Q 192 374, 184 376 Q 174 376, 170 360 Q 162 312, 156 268 Z"
            fill={skin}
          />
          <circle cx="73" cy="374" r="7" fill={skinShade} />
          <circle cx="187" cy="374" r="7" fill={skinShade} />
        </>
      ) : (
        <>
          <path
            d="M94 245 Q 82 300, 80 352 Q 78 360, 86 362 Q 94 362, 98 350 Q 104 300, 108 260 Z"
            fill={skin}
          />
          <path
            d="M166 245 Q 178 300, 180 352 Q 182 360, 174 362 Q 166 362, 162 350 Q 156 300, 152 260 Z"
            fill={skin}
          />
          <circle cx="83" cy="360" r="7" fill={skinShade} />
          <circle cx="177" cy="360" r="7" fill={skinShade} />
        </>
      )}

      {/* ---------- NECK ---------- */}
      <path d="M117 215 L 117 240 L 143 240 L 143 215 Z" fill={skin} />
      <path
        d="M117 226 L 143 226 L 143 240 L 117 240 Z"
        fill={skinDeep}
        opacity="0.25"
      />

      {/* ---------- NECKLACE ---------- */}
      {!isSilhouette && (
        <>
          <path
            d="M110 242 Q 130 254, 150 242"
            stroke={gold}
            strokeWidth="1.3"
            fill="none"
          />
          <circle cx="130" cy="251" r="2" fill={gold} />
        </>
      )}

      {/* ---------- HEAD (rounder, cuter) ---------- */}
      <ellipse cx="130" cy="170" rx="46" ry="52" fill={skin} />
      {/* subtle warm under-eye / cheek shading (kept very faint) */}
      {!isSilhouette && (
        <>
          <ellipse cx="104" cy="192" rx="7" ry="3.5" fill="#ffb7a8" opacity="0.35" />
          <ellipse cx="156" cy="192" rx="7" ry="3.5" fill="#ffb7a8" opacity="0.35" />
        </>
      )}

      {/* ---------- HAIR FRONT (crown) ---------- */}
      <path
        d="M84 130 Q 82 92, 130 84 Q 178 92, 176 130 Q 168 108, 152 104 Q 140 102, 130 102 Q 120 102, 108 104 Q 92 108, 84 130 Z"
        fill={hair}
      />
      {/* curtain bangs — soft wisps sweeping across forehead from center parting */}
      <path
        d="M128 108 Q 108 118, 92 132 Q 96 122, 104 116 Q 116 110, 128 108 Z"
        fill={hairHi}
      />
      <path
        d="M132 108 Q 152 118, 168 132 Q 164 122, 156 116 Q 144 110, 132 108 Z"
        fill={hairHi}
      />
      {/* a couple of stray strands for natural feel */}
      <path
        d="M114 116 Q 110 128, 112 138"
        stroke={hair}
        strokeWidth="1.2"
        fill="none"
        strokeLinecap="round"
        opacity="0.8"
      />
      <path
        d="M146 116 Q 150 128, 148 138"
        stroke={hair}
        strokeWidth="1.2"
        fill="none"
        strokeLinecap="round"
        opacity="0.8"
      />
      {/* side hair flowing */}
      <path d="M84 130 Q 76 200, 86 250 Q 92 220, 88 158 Z" fill={hair} />
      <path d="M176 130 Q 184 200, 174 250 Q 168 220, 172 158 Z" fill={hair} />

      {/* ---------- SUNGLASSES ON HEAD ---------- */}
      {!isSilhouette && (
        <g transform="translate(130 108)">
          <rect x="-30" y="-7" width="26" height="13" rx="6" fill={sunglass} opacity="0.95" />
          <rect x="4" y="-7" width="26" height="13" rx="6" fill={sunglass} opacity="0.95" />
          <rect x="-4" y="-3" width="8" height="2" fill={sunglass} />
          <rect x="-26" y="-5" width="6" height="3" rx="1" fill="#ffffff" opacity="0.55" />
          <rect x="8" y="-5" width="6" height="3" rx="1" fill="#ffffff" opacity="0.55" />
        </g>
      )}

      {/* ---------- FACE ---------- */}
      {!isSilhouette && (
        <>
          {/* THIN delicate eyebrows — higher up, far from eyes */}
          {isSad ? (
            <>
              <path
                d="M106 148 Q 115 146, 124 150"
                stroke={browColor}
                strokeWidth="1.2"
                fill="none"
                strokeLinecap="round"
              />
              <path
                d="M136 150 Q 145 146, 154 148"
                stroke={browColor}
                strokeWidth="1.2"
                fill="none"
                strokeLinecap="round"
              />
            </>
          ) : (
            <>
              <path
                d="M106 146 Q 115 142, 124 145"
                stroke={browColor}
                strokeWidth="1.1"
                fill="none"
                strokeLinecap="round"
              />
              <path
                d="M136 145 Q 145 142, 154 146"
                stroke={browColor}
                strokeWidth="1.1"
                fill="none"
                strokeLinecap="round"
              />
            </>
          )}

          {/* ---------- EYES (big, cute, expressive) ---------- */}
          {isHappy ? (
            // Closed crescent happy eyes — thinner, softer
            <>
              <path
                d="M106 170 Q 116 164, 126 170"
                stroke={browColor}
                strokeWidth="1.8"
                fill="none"
                strokeLinecap="round"
              />
              <path
                d="M134 170 Q 144 164, 154 170"
                stroke={browColor}
                strokeWidth="1.8"
                fill="none"
                strokeLinecap="round"
              />
              {/* tiny side lashes */}
              <path d="M106 168 l -1.5 -1" stroke={browColor} strokeWidth="0.9" strokeLinecap="round" opacity="0.8" />
              <path d="M126 168 l 1.5 -1" stroke={browColor} strokeWidth="0.9" strokeLinecap="round" opacity="0.8" />
              <path d="M134 168 l -1.5 -1" stroke={browColor} strokeWidth="0.9" strokeLinecap="round" opacity="0.8" />
              <path d="M154 168 l 1.5 -1" stroke={browColor} strokeWidth="0.9" strokeLinecap="round" opacity="0.8" />
            </>
          ) : (
            <>
              {/* Left eye */}
              <g>
                <ellipse
                  cx="116"
                  cy="171"
                  rx="7"
                  ry={isSad ? 4.5 : 6}
                  fill="#ffffff"
                />
                {/* iris — warm brown */}
                <ellipse
                  cx="116"
                  cy={isSad ? 173 : 171}
                  rx="4.2"
                  ry={isSad ? 3.8 : 5}
                  fill="#5a3220"
                />
                <ellipse
                  cx="116"
                  cy={isSad ? 173 : 171}
                  rx="2.4"
                  ry={isSad ? 2.2 : 2.8}
                  fill="#3a1a0a"
                />
                {/* sparkle highlights — cute eyes */}
                <circle cx="118" cy={isSad ? 171 : 169} r="1.2" fill="#fff" />
                <circle cx="114" cy={isSad ? 174 : 173} r="0.7" fill="#fff" />
                {/* upper lash line — soft brown, thin */}
                <path
                  d={isSad ? "M109 170 Q 116 167, 123 170" : "M109 167 Q 116 163, 123 167"}
                  stroke={browColor}
                  strokeWidth="1.1"
                  fill="none"
                  strokeLinecap="round"
                  opacity="0.85"
                />
                {/* corner lashes */}
                <path d="M108 168 l -1.5 -0.5" stroke={browColor} strokeWidth="0.8" strokeLinecap="round" opacity="0.7" />
                <path d="M124 168 l 1.5 -0.5" stroke={browColor} strokeWidth="0.8" strokeLinecap="round" opacity="0.7" />
              </g>

              {/* Right eye */}
              <g>
                <ellipse
                  cx="144"
                  cy="171"
                  rx="7"
                  ry={isSad ? 4.5 : 6}
                  fill="#ffffff"
                />
                <ellipse
                  cx="144"
                  cy={isSad ? 173 : 171}
                  rx="4.2"
                  ry={isSad ? 3.8 : 5}
                  fill="#5a3220"
                />
                <ellipse
                  cx="144"
                  cy={isSad ? 173 : 171}
                  rx="2.4"
                  ry={isSad ? 2.2 : 2.8}
                  fill="#3a1a0a"
                />
                <circle cx="146" cy={isSad ? 171 : 169} r="1.2" fill="#fff" />
                <circle cx="142" cy={isSad ? 174 : 173} r="0.7" fill="#fff" />
                <path
                  d={isSad ? "M137 170 Q 144 167, 151 170" : "M137 167 Q 144 163, 151 167"}
                  stroke={browColor}
                  strokeWidth="1.1"
                  fill="none"
                  strokeLinecap="round"
                  opacity="0.85"
                />
                <path d="M136 168 l -1.5 -0.5" stroke={browColor} strokeWidth="0.8" strokeLinecap="round" opacity="0.7" />
                <path d="M152 168 l 1.5 -0.5" stroke={browColor} strokeWidth="0.8" strokeLinecap="round" opacity="0.7" />
              </g>
            </>
          )}

          {/* ---------- Tears when sad ---------- */}
          {isSad && (
            <>
              <path
                d="M114 178 Q 112 192, 110 204"
                stroke="#9ec8ff"
                strokeWidth="2.2"
                fill="none"
                strokeLinecap="round"
                opacity="0.9"
              />
              <ellipse cx="110" cy="206" rx="2.6" ry="3.6" fill="#9ec8ff" opacity="0.95" />
              <path
                d="M146 178 Q 148 192, 150 204"
                stroke="#9ec8ff"
                strokeWidth="2.2"
                fill="none"
                strokeLinecap="round"
                opacity="0.9"
              />
              <ellipse cx="150" cy="206" rx="2.6" ry="3.6" fill="#9ec8ff" opacity="0.95" />
            </>
          )}

          {/* ---------- Cute small nose ---------- */}
          <path
            d="M128 184 Q 130 192, 132 188"
            stroke={skinDeep}
            strokeWidth="1.2"
            fill="none"
            strokeLinecap="round"
            opacity="0.7"
          />

          {/* ---------- MOUTH ---------- */}
          {isHappy ? (
            <>
              {/* big smile with teeth */}
              <path
                d="M114 200 Q 130 220, 146 200 Q 130 214, 114 200 Z"
                fill={lipDeep}
              />
              <path
                d="M118 202 Q 130 210, 142 202 L 142 204 Q 130 208, 118 204 Z"
                fill="#ffffff"
              />
              <path
                d="M114 200 Q 130 220, 146 200"
                stroke={lipDeep}
                strokeWidth="1.5"
                fill="none"
              />
            </>
          ) : isSad ? (
            // small frown
            <>
              <path
                d="M122 206 Q 130 200, 138 206"
                stroke={lipDeep}
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
              />
            </>
          ) : (
            // neutral soft pout — heart-ish full lips with gloss
            <>
              <path
                d="M122 200 Q 126 196, 130 199 Q 134 196, 138 200 Q 134 207, 130 207 Q 126 207, 122 200 Z"
                fill={lip}
              />
              <path
                d="M125 199 Q 130 201, 135 199"
                stroke="#fff"
                strokeWidth="0.8"
                fill="none"
                opacity="0.7"
              />
            </>
          )}

          {/* ---------- Cheek blush (happy) ---------- */}
          {isHappy && (
            <>
              <ellipse cx="102" cy="190" rx="9" ry="5.5" fill="#ff8fab" opacity="0.6" />
              <ellipse cx="158" cy="190" rx="9" ry="5.5" fill="#ff8fab" opacity="0.6" />
            </>
          )}

          {/* ---------- Sparkles when happy ---------- */}
          {isHappy && (
            <g fill="#fff9c4">
              <path d="M88 150 l 2 5 l 5 2 l -5 2 l -2 5 l -2 -5 l -5 -2 l 5 -2 z" />
              <path d="M172 148 l 1.6 4 l 4 1.6 l -4 1.6 l -1.6 4 l -1.6 -4 l -4 -1.6 l 4 -1.6 z" />
              <path d="M178 192 l 1.2 3 l 3 1.2 l -3 1.2 l -1.2 3 l -1.2 -3 l -3 -1.2 l 3 -1.2 z" />
              <path d="M82 198 l 1.2 3 l 3 1.2 l -3 1.2 l -1.2 3 l -1.2 -3 l -3 -1.2 l 3 -1.2 z" />
            </g>
          )}

          {/* ---------- Gold hoop earrings ---------- */}
          <circle cx="84" cy="180" r="5" stroke={gold} strokeWidth="1.7" fill="none" />
          <circle cx="176" cy="180" r="5" stroke={gold} strokeWidth="1.7" fill="none" />

          {/* ---------- Rose in hair when happy ---------- */}
          {isHappy && (
            <g transform="translate(93 102)">
              <circle r="9" fill="#c0102f" />
              <circle r="5" fill="#ff4d6d" />
              <circle r="2" fill="#ff8fab" />
              <path d="M -8 4 Q -14 -2, -10 -8" fill="#1f5132" />
            </g>
          )}
        </>
      )}
    </svg>
  );
};

export default HetviGirl;
